/*
  Turns the family's lessons plus the reminder choice into the alarms a phone keeps by itself.
  Pure functions, shared by Android (ClassTutorAlarm plugin), iPhone (AlarmKit + notifications)
  and the in-app ringer. Same idea as Tazkiyah's reminderPlan.ts, but lessons are one-off events
  at exact times rather than weekly repeats, so each alarm carries an `at` timestamp.
*/
import type { Child, Lesson, ReminderPrefs } from "app/services/types";

export interface PhoneAlarm {
  /** Stable per lesson, so re-planning replaces rather than duplicates. */
  id: string;
  registerId: number;
  mode: "notification" | "alarm" | "both";
  title: string;
  body: string;
  /** When to ring, epoch milliseconds. */
  at: number;
  sound: boolean;
  vibrate: boolean;
  /** Page of the app to open from the alarm. */
  path: string;
}

export const PLAN_DAYS_AHEAD = 14;
export const MAX_ALARMS = 150;

const fmt = new Intl.DateTimeFormat("en-GB", { hour: "numeric", minute: "2-digit", hour12: true });
const timeOf = (iso: string) => fmt.format(new Date(iso)).replace(" ", "").toLowerCase();

export function alarmText(lesson: Lesson, names: string[], minutesBefore: number) {
  const when = minutesBefore === 0 ? "is starting now" : `starts in ${minutesBefore} minutes`;
  const title = `${lesson.year ? `${lesson.year} ` : ""}${lesson.subject} ${when}`;
  const who = names.length ? `${names.join(" and ")} · ` : "";
  const body = `${who}${timeOf(lesson.start)}${lesson.teacher ? ` with ${lesson.teacher}` : ""}. Tap to join.`;
  return { title, body };
}

export function planLessonAlarms(lessons: Lesson[], children: Child[], prefs: ReminderPrefs, now = Date.now()): PhoneAlarm[] {
  if (!prefs.enabled) return [];
  const kids = new Map(children.map((c) => [c.id, c]));
  const horizon = now + PLAN_DAYS_AHEAD * 86400000;
  const out: PhoneAlarm[] = [];
  for (const lesson of lessons) {
    if (lesson.cancelled) continue;
    const start = new Date(lesson.start).getTime();
    const at = start - prefs.minutesBefore * 60000;
    if (at <= now + 5000 || at > horizon) continue;
    const attending = lesson.students.filter((s) => !s.onLeave && kids.has(s.id));
    if (!attending.length) continue;
    const names = attending.map((s) => kids.get(s.id)!.name);
    const { title, body } = alarmText(lesson, names, prefs.minutesBefore);
    out.push({ id: `lesson-${lesson.registerId}`, registerId: lesson.registerId, mode: prefs.mode, title, body, at, sound: prefs.sound, vibrate: prefs.vibrate, path: `/lesson/${lesson.registerId}` });
  }
  return out.sort((a, b) => a.at - b.at).slice(0, MAX_ALARMS);
}

/** The alarm that should be ringing right now, if any (for the in-app overlay). */
export function dueNow(alarms: PhoneAlarm[], now = Date.now(), windowMs = 60000): PhoneAlarm | null {
  return alarms.find((a) => now >= a.at && now - a.at < windowMs) ?? null;
}
